import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { persistReducer, type StateReconciler } from 'redux-persist'
import autoMergeLevel1 from 'redux-persist/lib/stateReconciler/autoMergeLevel1'
import storage from 'redux-persist/lib/storage'

import { type CardFilterType } from '@/defs/cardFilters'
import {
  type GalleryColumnSlugType,
  type GalleryGroupbySlugType,
  type GalleryLockSlugType,
  type GallerySizeSlugType,
  type GallerySortbyDirectionType,
  type GallerySortbySlugType,
  type GalleryViewSlugType,
} from '@/defs/gallery'
import { type RecRoleType } from '@/defs/rec'
import { cleanGalleryFilters } from '@/utils/galleryUtil'

import { GALLERY_PERSIST_VERSION, galleryMigrate } from './migrations/galleryMigrate'

export type GalleryStateType = {
  cardFilters?: CardFilterType[]
  columnsPref?: GalleryColumnSlugType[]
  groupbyPref?: GalleryGroupbySlugType
  key?: string
  lockedPrefSlugs?: Partial<Record<GalleryLockSlugType, true>>
  rolePref?: RecRoleType
  sizePref?: GallerySizeSlugType
  sortbyDirectionPref?: GallerySortbyDirectionType
  sortbyPref?: GallerySortbySlugType
  viewPref?: GalleryViewSlugType
}

// Persists prefs only if they are listed in the lockedPrefSlugs object
const persistLockedPrefs = ({
  cardFilters,
  groupbyPref,
  sortbyDirectionPref,
  sortbyPref,
  ...s
}: GalleryStateType): GalleryStateType => ({
  ...s,
  ...(s.lockedPrefSlugs?.cardFilters && { cardFilters }),
  ...(s.lockedPrefSlugs?.groupby && { groupbyPref }),
  ...(s.lockedPrefSlugs?.sortby && { sortbyDirectionPref, sortbyPref }),
})

const initialState: GalleryStateType = {}

export const gallerySlice = createSlice({
  initialState,
  name: 'gallery',
  reducers: {
    reset: (s) => ({ key: s.key }),
    resetSortby: (s) => {
      s.sortbyPref = undefined
      s.sortbyDirectionPref = undefined
    },
    setCardFilters: (s, action: PayloadAction<CardFilterType[]>) => {
      s.cardFilters = cleanGalleryFilters(action.payload)
    },
    setColumns: (s, action: PayloadAction<GalleryColumnSlugType[]>) => {
      s.columnsPref = action.payload
    },
    setGroupby: (s, action: PayloadAction<GalleryGroupbySlugType>) => {
      s.groupbyPref = action.payload
    },
    setKey: (s, action: PayloadAction<string>) => {
      if (s.key === action.payload) return
      return { ...persistLockedPrefs(s), key: action.payload }
    },
    setLockedPrefSlug: (s, action: PayloadAction<{ isLocked: boolean; slug: GalleryLockSlugType }>) => {
      s.lockedPrefSlugs ??= {}
      if (action.payload.isLocked) s.lockedPrefSlugs[action.payload.slug] = true
      else delete s.lockedPrefSlugs[action.payload.slug]
    },
    setRole: (s, action: PayloadAction<RecRoleType>) => {
      s.rolePref = action.payload
    },
    setSize: (s, action: PayloadAction<GallerySizeSlugType>) => {
      s.sizePref = action.payload
    },
    setSortby: (s, action: PayloadAction<GallerySortbySlugType>) => {
      s.sortbyPref = action.payload
    },
    setSortbyDirection: (s, action: PayloadAction<GallerySortbyDirectionType>) => {
      s.sortbyDirectionPref = action.payload
    },
    setView: (s, action: PayloadAction<GalleryViewSlugType>) => {
      s.viewPref = action.payload
    },
  },
})

// NOTE: Reconciles the state with the persisted state
const stateReconciler: StateReconciler<GalleryStateType & { _persist?: { rehydrated?: boolean } }> = (
  inboundState,
  originalState,
  reducedState,
  config,
) => {
  const inboundCleaned = { ...inboundState, cardFilters: cleanGalleryFilters(inboundState.cardFilters) }

  // NOTE: Only maintains the persisted prefs that are specifically locked
  const inboundPersisted = persistLockedPrefs(inboundCleaned)

  return autoMergeLevel1(inboundPersisted, originalState, reducedState, config)
}

const persistConfig = {
  key: 'gallery',
  migrate: galleryMigrate,
  stateReconciler,
  storage,
  version: GALLERY_PERSIST_VERSION,
  whitelist: [
    'cardFilters',
    'columnsPref',
    'groupbyPref',
    'lockedPrefSlugs',
    'sizePref',
    'sortbyPref',
    'sortbyDirectionPref',
    'viewPref',
  ],
}

export const galleryActions = gallerySlice.actions
export const galleryReducer = persistReducer(persistConfig, gallerySlice.reducer)
